import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, Save, Loader, CheckCircle, AlertCircle, 
  ExternalLink, Settings, HelpCircle, FileSignature,
  Shield, Mail, Clock, Eye, Smartphone, PenTool,
  ChevronRight, Sparkles, Lock, Users
} from 'lucide-react';
import { useBusinessContext } from '../contexts/BusinessContext';
import api from '../services/api';

// ============================================
// INDUSTRY THEME COLORS (fallback if no brand color)
// ============================================
const industryThemes = {
  'pressure-washing': { primary: '#7c3aed', light: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
  'landscaping': { primary: '#059669', light: '#d1fae5', border: '#6ee7b7', text: '#047857' },
  'pool-service': { primary: '#0284c7', light: '#e0f2fe', border: '#7dd3fc', text: '#0369a1' },
  'cleaning': { primary: '#0891b2', light: '#cffafe', border: '#67e8f9', text: '#0e7490' },
  'hvac': { primary: '#dc2626', light: '#fee2e2', border: '#fca5a5', text: '#b91c1c' },
  'plumbing': { primary: '#2563eb', light: '#dbeafe', border: '#93c5fd', text: '#1d4ed8' },
  'electrical': { primary: '#d97706', light: '#fef3c7', border: '#fcd34d', text: '#b45309' },
  'roofing': { primary: '#78716c', light: '#f5f5f4', border: '#d6d3d1', text: '#57534e' },
  'painting': { primary: '#8b5cf6', light: '#ede9fe', border: '#c4b5fd', text: '#7c3aed' },
  'general': { primary: '#6366f1', light: '#e0e7ff', border: '#a5b4fc', text: '#4f46e5' },
  'default': { primary: '#7c3aed', light: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' }
};

// Helper to generate color variants from a hex color
const generateColorVariants = (hexColor) => {
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return {
    primary: hexColor,
    light: `rgba(${r}, ${g}, ${b}, 0.1)`,
    border: `rgba(${r}, ${g}, ${b}, 0.3)`,
    text: hexColor,
    // For gradients
    lightRgb: `${r}, ${g}, ${b}`
  };
};

// ============================================
// TOOLTIP COMPONENT - Same style as TaxSettingsTab
// ============================================
const Tooltip = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };
  
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="ml-1.5 p-1 text-gray-400 hover:text-blue-600 focus:outline-none rounded-full hover:bg-blue-50 active:bg-blue-100"
        aria-label="More information"
      >
        <HelpCircle size={14} />
      </button>
      
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30"
          onClick={handleClose}
        >
          <div 
            className="bg-gray-900 text-white text-sm rounded-xl shadow-2xl max-w-sm w-full p-4 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {title && (
              <p className="font-semibold text-base mb-2 text-blue-300">{title}</p>
            )}
            <div className="text-gray-200 space-y-2">{children}</div>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Agreement Settings Tab Component
 * For use in the Settings page - manages agreement preferences
 * Enhanced with tooltips and beginner-friendly explanations
 */
const AgreementSettingsTab = ({ showToast }) => {
  const { subscriptionPlan, isTrialActive, business, brandColor: contextBrandColor } = useBusinessContext();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [businessSettings, setBusinessSettings] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    requireAgreementForJobs: false,
    defaultAgreementTemplateId: null
  });
  
  // Track if settings changed
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState(null);

  // Get theme colors - prioritize brand color, fall back to industry theme
  const theme = useMemo(() => {
    // Try multiple sources for brand color
    const brandColor = contextBrandColor || 
                       business?.brandColor || 
                       business?.BrandColor || 
                       businessSettings?.brandColor ||
                       businessSettings?.BrandColor;
    
    if (brandColor) {
      return generateColorVariants(brandColor);
    }
    
    const industry = business?.industryType || business?.IndustryType || 'default';
    return industryThemes[industry] || industryThemes.default;
  }, [contextBrandColor, business, businessSettings]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (originalSettings) {
      const changed = 
        settings.requireAgreementForJobs !== originalSettings.requireAgreementForJobs ||
        settings.defaultAgreementTemplateId !== originalSettings.defaultAgreementTemplateId;
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load templates and business settings in parallel
      const [templatesRes, businessRes] = await Promise.all([
        api.get('/serviceagreements/templates'),
        api.get('/business/settings')
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      
      // Store full business settings (includes brandColor)
      setBusinessSettings(businessRes.data);
      
      const agreementSettings = {
        requireAgreementForJobs: businessRes.data.requireAgreementForJobs || false,
        defaultAgreementTemplateId: businessRes.data.defaultAgreementTemplateId || null
      };
      
      setSettings(agreementSettings);
      setOriginalSettings(agreementSettings);
    } catch (err) {
      console.error('Error loading agreement settings:', err);
      showToast?.('Failed to load agreement settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await api.put('/business/settings', {
        requireAgreementForJobs: settings.requireAgreementForJobs,
        defaultAgreementTemplateId: settings.defaultAgreementTemplateId
      });
      
      setOriginalSettings({ ...settings });
      setHasChanges(false);
      showToast?.('Agreement settings saved successfully');
    } catch (err) {
      console.error('Error saving agreement settings:', err);
      showToast?.('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRequire = () => {
    setSettings(prev => ({
      ...prev,
      requireAgreementForJobs: !prev.requireAgreementForJobs
    }));
  };

  const handleTemplateChange = (e) => {
    const value = e.target.value;
    setSettings(prev => ({
      ...prev,
      defaultAgreementTemplateId: value ? parseInt(value) : null
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  const accessibleTemplates = templates.filter(t => t.isAccessible);
  const selectedTemplate = templates.find(t => t.id === settings.defaultAgreementTemplateId);

  return (
    <div className="space-y-6">
      
      {/* ============================================ */}
      {/* HERO SECTION - What are Service Agreements? */}
      {/* ============================================ */}
      <div 
        className="card border-2"
        style={{ 
          background: `linear-gradient(135deg, ${theme.light} 0%, rgba(${theme.lightRgb || '124, 58, 237'}, 0.05) 100%)`,
          borderColor: theme.border
        }}
      >
        <div className="flex flex-col sm:flex-row items-start gap-4">
          {/* Icon - Hidden on mobile to save space */}
          <div 
            className="hidden sm:flex p-3 rounded-xl"
            style={{ backgroundColor: `${theme.primary}20` }}
          >
            <FileSignature className="h-7 w-7" style={{ color: theme.primary }} />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center flex-wrap gap-2">
              Service Agreements
              <Tooltip title="What is a Service Agreement?">
                <p>A service agreement is a <strong>digital contract</strong> between you and your customer that outlines:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-gray-300">
                  <li>Scope of work being performed</li>
                  <li>Payment terms and conditions</li>
                  <li>Cancellation policies</li>
                  <li>Liability and damage clauses</li>
                </ul>
                <p className="mt-3 text-purple-300">💡 Having customers sign before work begins protects both you AND them!</p>
              </Tooltip>
            </h2>
            <p className="text-gray-600 mb-4">
              Protect your business with professional digital contracts. Customers sign 
              electronically via a secure link — no printing, scanning, or chasing paperwork!
            </p>
            
            {/* Quick Stats / Benefits */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Shield className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span>Legal Protection</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Smartphone className="h-4 w-4 flex-shrink-0" style={{ color: theme.primary }} />
                <span>Sign on Any Device</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <span>Takes 30 Seconds</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Lock className="h-4 w-4 flex-shrink-0" style={{ color: theme.primary }} />
                <span>No Login Needed</span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <a
                href="/agreements"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm hover:opacity-90"
                style={{ backgroundColor: theme.primary }}
              >
                <FileText className="h-4 w-4" />
                View My Agreements
                <ChevronRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* HOW IT WORKS - Visual Steps */}
      {/* ============================================ */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-amber-500" />
          How It Works
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Step 1 */}
          <div className="relative p-4 bg-gray-50 rounded-xl">
            <div 
              className="absolute -top-3 -left-2 w-7 h-7 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md"
              style={{ backgroundColor: theme.primary }}
            >
              1
            </div>
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-5 w-5" style={{ color: theme.primary }} />
                <span className="font-semibold text-gray-900">Customer Accepts Quote</span>
              </div>
              <p className="text-sm text-gray-600">
                When a customer accepts your quote online, they'll automatically see your service agreement to review and sign.
              </p>
            </div>
          </div>
          
          {/* Step 2 */}
          <div className="relative p-4 bg-gray-50 rounded-xl">
            <div 
              className="absolute -top-3 -left-2 w-7 h-7 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md"
              style={{ backgroundColor: theme.primary }}
            >
              2
            </div>
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-2">
                <PenTool className="h-5 w-5" style={{ color: theme.primary }} />
                <span className="font-semibold text-gray-900">Customer Signs</span>
              </div>
              <p className="text-sm text-gray-600">
                They draw or type their signature right on their phone or computer. No app download or account creation needed!
              </p>
            </div>
          </div>
          
          {/* Step 3 */}
          <div className="relative p-4 bg-gray-50 rounded-xl">
            <div 
              className="absolute -top-3 -left-2 w-7 h-7 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md"
              style={{ backgroundColor: theme.primary }}
            >
              3
            </div>
            <div className="pt-2">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-gray-900">You're Protected</span>
              </div>
              <p className="text-sm text-gray-600">
                The signed agreement is stored with their signature, IP address, and timestamp — legal proof they agreed!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SETTINGS CARD */}
      {/* ============================================ */}
      <div className="card">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Agreement Settings</h3>
        </div>

        <div className="space-y-6">
          
          {/* ============================================ */}
          {/* TOGGLE: Require Agreement for Jobs */}
          {/* ============================================ */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <label className="font-semibold text-gray-900 flex items-center flex-wrap">
                  Require Agreement When Accepting Quotes
                  <Tooltip title="Should I enable this?">
                    <p><strong>Yes!</strong> We strongly recommend enabling this for most service businesses.</p>
                    <p className="mt-2"><strong>When ON:</strong> Customers will see your service agreement when they accept a quote online. They must sign before their job is confirmed.</p>
                    <p className="mt-2"><strong>When OFF:</strong> Quotes are accepted without an agreement. You can still send agreements manually from the Agreements page.</p>
                    <p className="mt-3 text-green-300">✓ Protects you from disputes</p>
                    <p className="text-green-300">✓ Sets clear expectations</p>
                    <p className="text-green-300">✓ Looks professional</p>
                  </Tooltip>
                </label>
                <p className="text-sm text-gray-600 mt-1.5">
                  When customers accept a quote online, they'll review and sign your agreement before confirming.
                </p>
                
                {/* Status indicator */}
                <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                  settings.requireAgreementForJobs 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {settings.requireAgreementForJobs ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Agreements required at quote acceptance
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4" />
                      Agreements are optional
                    </>
                  )}
                </div>
              </div>
              
              {/* Toggle Switch */}
              <button
                type="button"
                onClick={handleToggleRequire}
                className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                style={{ 
                  backgroundColor: settings.requireAgreementForJobs ? theme.primary : '#d1d5db',
                  '--tw-ring-color': theme.primary
                }}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                    settings.requireAgreementForJobs ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* ============================================ */}
          {/* DROPDOWN: Default Template Selection */}
          {/* ============================================ */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <label className="block font-semibold text-gray-900 mb-1.5 flex items-center flex-wrap">
              Default Agreement Template
              <Tooltip title="What template should I use?">
                <p>Templates are <strong>pre-written agreements</strong> that include important legal language for your industry.</p>
                <p className="mt-2"><strong>General Service Agreement:</strong> Good for most businesses - covers basic terms, liability, and payment.</p>
                <p className="mt-2"><strong>Industry-specific:</strong> Some templates include terms specific to pressure washing, landscaping, etc.</p>
                <p className="mt-3 text-amber-300">💡 You can preview and customize templates on the Agreements page.</p>
              </Tooltip>
            </label>
            <p className="text-sm text-gray-600 mb-3">
              This template will be shown to customers when they accept quotes.
            </p>
            
            <select
              value={settings.defaultAgreementTemplateId || ''}
              onChange={handleTemplateChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base transition-colors"
              style={{ 
                '--tw-ring-color': theme.primary,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = theme.primary;
                e.target.style.boxShadow = `0 0 0 3px ${theme.light}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">Select a template...</option>
              {accessibleTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
            
            {/* Selected template info */}
            {selectedTemplate && (
              <div 
                className="mt-3 p-3 rounded-lg border"
                style={{ backgroundColor: theme.light, borderColor: theme.border }}
              >
                <div className="flex items-start gap-2">
                  <FileText className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: theme.primary }} />
                  <div>
                    <p className="font-medium" style={{ color: theme.text }}>{selectedTemplate.name}</p>
                    {selectedTemplate.description && (
                      <p className="text-sm mt-0.5" style={{ color: theme.text, opacity: 0.8 }}>{selectedTemplate.description}</p>
                    )}
                    <a 
                      href="/agreements" 
                      className="text-sm font-medium mt-2 inline-flex items-center gap-1 hover:opacity-80"
                      style={{ color: theme.primary }}
                    >
                      Preview or customize this template
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            )}
            
            {/* Warning if enabled but no template selected */}
            {settings.requireAgreementForJobs && !settings.defaultAgreementTemplateId && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">No template selected</p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      Please select a default template above so customers can sign when accepting quotes.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ============================================ */}
          {/* SAVE BUTTON */}
          {/* ============================================ */}
          {hasChanges && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-amber-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                You have unsaved changes
              </p>
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 text-white font-semibold rounded-lg disabled:bg-gray-300 transition-colors shadow-sm hover:opacity-90"
                style={{ backgroundColor: saving ? undefined : theme.primary }}
              >
                {saving ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* TEMPLATES LIST */}
      {/* ============================================ */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          Available Templates
          <Tooltip title="About Templates">
            <p>Templates are <strong>pre-written agreements</strong> that you can use as-is or customize.</p>
            <p className="mt-2"><strong>System templates:</strong> Created by SuddyFlow with industry best practices.</p>
            <p className="mt-2"><strong>Custom templates:</strong> Pro and Enterprise users can create their own templates or modify existing ones.</p>
          </Tooltip>
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          These are the agreement templates available on your plan.
        </p>
        
        <div className="space-y-3">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No templates available</p>
            </div>
          ) : (
            templates.map(template => (
              <div 
                key={template.id}
                className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border gap-3 transition-colors ${
                  template.isAccessible 
                    ? 'bg-white border-gray-200' 
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
                style={template.isAccessible ? {
                  ':hover': { borderColor: theme.border, backgroundColor: theme.light }
                } : {}}
                onMouseEnter={(e) => {
                  if (template.isAccessible) {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.backgroundColor = theme.light;
                  }
                }}
                onMouseLeave={(e) => {
                  if (template.isAccessible) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <FileText 
                    className="h-5 w-5 flex-shrink-0 mt-0.5" 
                    style={{ color: template.isAccessible ? theme.primary : '#9ca3af' }}
                  />
                  <div>
                    <p className={`font-medium ${template.isAccessible ? 'text-gray-900' : 'text-gray-500'}`}>
                      {template.name}
                    </p>
                    {template.description && (
                      <p className="text-sm text-gray-500 mt-0.5">{template.description}</p>
                    )}
                    {/* Feature badges */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {template.includePhotoConsent && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Photo Consent
                        </span>
                      )}
                      {template.includeCancellationPolicy && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                          Cancellation Policy
                        </span>
                      )}
                      {template.includeWeatherPolicy && (
                        <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                          Weather Policy
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-8 sm:ml-0">
                  {template.isAccessible ? (
                    <>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Available
                      </span>
                      <a 
                        href="/agreements"
                        className="p-2 text-gray-400 rounded-lg transition-colors"
                        title="Preview template"
                        style={{ ':hover': { color: theme.primary, backgroundColor: theme.light } }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = theme.primary;
                          e.currentTarget.style.backgroundColor = theme.light;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#9ca3af';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </a>
                    </>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      {template.requiredTier} Plan
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Upgrade CTA for Solo users */}
        {!isTrialActive && subscriptionPlan === 'Solo' && (
          <div 
            className="mt-6 p-4 rounded-xl border"
            style={{ 
              background: `linear-gradient(135deg, ${theme.light} 0%, rgba(${theme.lightRgb || '124, 58, 237'}, 0.05) 100%)`,
              borderColor: theme.border
            }}
          >
            <div className="flex items-start gap-3">
              <div 
                className="p-2 rounded-lg"
                style={{ backgroundColor: `${theme.primary}20` }}
              >
                <Users className="h-5 w-5" style={{ color: theme.primary }} />
              </div>
              <div>
                <p className="font-semibold" style={{ color: theme.text }}>Want more templates?</p>
                <p className="text-sm mt-1" style={{ color: theme.text, opacity: 0.8 }}>
                  Upgrade to <strong>Pro</strong> to customize existing templates, or <strong>Enterprise</strong> to create 
                  your own from scratch with your exact legal language.
                </p>
                <a 
                  href="/settings"
                  onClick={() => {
                    // Try to switch to subscription tab
                    setTimeout(() => {
                      document.querySelector('[data-tab="subscription"]')?.click();
                    }, 100);
                  }}
                  className="inline-flex items-center gap-1 text-sm font-semibold mt-2 hover:opacity-80"
                  style={{ color: theme.primary }}
                >
                  View Plans
                  <ChevronRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* FAQ / COMMON QUESTIONS */}
      {/* ============================================ */}
      <div className="card bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-gray-400" />
          Common Questions
        </h3>
        
        <div className="space-y-4">
          <div>
            <p className="font-medium text-gray-900">Do my customers need to create an account?</p>
            <p className="text-sm text-gray-600 mt-1">
              No! Customers receive a secure link via email and can sign directly on any device without 
              logging in or downloading anything.
            </p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">Are these agreements legally binding?</p>
            <p className="text-sm text-gray-600 mt-1">
              Yes. Electronic signatures are legally binding under the ESIGN Act. We capture the signer's 
              name, IP address, timestamp, and signature image as proof of agreement.
            </p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">What if a customer books by phone instead of online?</p>
            <p className="text-sm text-gray-600 mt-1">
              You can manually send an agreement from the{' '}
              <a 
                href="/agreements" 
                className="font-medium hover:underline"
                style={{ color: theme.primary }}
              >
                Agreements page
              </a>. 
              Just select the customer and template, and they'll receive an email with a signing link.
            </p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">Can I see who has and hasn't signed?</p>
            <p className="text-sm text-gray-600 mt-1">
              Absolutely! The Agreements page shows all pending, signed, and expired agreements. You can 
              also resend reminders to customers who haven't signed yet.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementSettingsTab;